/**
 * CORE-1146 — the one-per-second tick behind every countdown on the Live
 * surfaces («СКОРО НАЧНЁТСЯ», «до начала MM:SS», «Начинаем через N секунд»).
 *
 * Two things a plain `setInterval(…, 1000)` gets wrong for a clock, both of
 * them visible to the user as «таймер идёт медленнее реальных секунд»:
 *
 *  1. **Its error accumulates.** Every fire is late by whatever the callback
 *     costs (here: a render of the room screen), and `setInterval` measures the
 *     next delay from the moment it fires, not from the moment it was due. A
 *     display of `floor(remaining / 1000)` therefore holds one value for
 *     1000 + δ ms, and once the accumulated δ crosses a second boundary the
 *     countdown visibly skips a number. Scheduling each tick at the *next whole
 *     second of the wall clock* keeps the error bounded instead: a late tick
 *     shortens the following delay rather than pushing it.
 *  2. **It says nothing when the clock jumps.** A hidden tab is throttled and a
 *     backgrounded native app is frozen outright, so on return the last tick can
 *     be minutes old. Everything built on this helper re-reads `Date.now()`
 *     rather than counting its own ticks, so one immediate tick on foreground is
 *     all it takes to land back on the truth — no catching-up, no drift.
 *
 * The callback must derive what it shows from an absolute anchor
 * (`scheduledAtUtc`, `startedAtUtc`) — this helper deliberately passes no
 * «elapsed» of its own, because a counter that adds up local ticks is the bug
 * this module exists to remove.
 */
import { subscribeAppForeground } from './appForeground';

/**
 * Starts a wall-clock-aligned 1 Hz tick. Returns the stop function — call it
 * from the effect cleanup exactly like `clearInterval`; it also unbinds the
 * foreground listener, so a caller that stops has nothing left running.
 *
 * @param onTick called once per second, and immediately whenever the app/tab
 *               comes back to the foreground.
 * @param now    injected in tests; defaults to the real clock.
 */
export function startSecondTick(onTick: () => void, now: () => number = Date.now): () => void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const schedule = () => {
        if (stopped) return;
        // (0, 1000] ms — always the next whole second, never «1000 from here».
        const delay = 1000 - (now() % 1000);
        timer = setTimeout(() => {
            timer = null;
            if (stopped) return;
            onTick();
            schedule();
        }, delay);
    };

    const resync = () => {
        if (stopped) return;
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        // Whatever is on screen was computed before the sleep — refresh it now
        // and re-align, instead of waiting out the remainder of a stale delay.
        onTick();
        schedule();
    };

    schedule();
    const unsubscribe = subscribeAppForeground(resync);

    return () => {
        stopped = true;
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        unsubscribe();
    };
}
